import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Archive,
  ArrowLeft,
  ArrowRight,
  CalendarBlank,
  MapPin,
  PencilSimple,
  Check,
  X,
  Trash,
  User,
  ArrowSquareOut,
  Printer,
  Wrench,
  DotsThreeVertical,
  Plus,
  Receipt,
  SquaresFour,
  Stack,
  ChatCircle,
  EnvelopeSimple,
  Images,
} from "@phosphor-icons/react";
import { SegTabs, useSwipeTabs } from "@/components/SegTabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoveAccountDialog } from "@/components/MoveAccountDialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AppHeader } from "@/components/AppHeader";
import { AddressCombo } from "@/components/AddressCombo";
import { AssistantDock } from "@/components/AssistantDock";
import { ConfirmTextDialog } from "@/components/ConfirmTextDialog";
import { FileVault } from "@/components/FileVault";
import { TemplatorCard } from "@/components/TemplatorCard";
import { JobPhotos } from "@/components/JobPhotos";
import { JobFinishedPhotos } from "@/components/JobFinishedPhotos";
import { SinkAssign } from "@/components/SinkAssign";
import { DateField } from "@/components/DateTimeField";
import { Input } from "@/components/ui/input";
import { JOB_STAGES, STAGE_DOT, StageBadge } from "@/components/StageBadge";
import { StageAge } from "@/components/StageAge";
import { DrawingApprovalBadge } from "@/components/DrawingApprovalBadge";
import { SmsPanel } from "@/components/SmsPanel";
import { JobEmailThread } from "@/components/JobEmailThread";
import { PortalThread } from "@/components/PortalThread";
import { FixturesPanel } from "@/components/FixturesPanel";
import { TaskDialog } from "@/components/TaskDialog";
import { ExtraStopCard } from "@/components/ExtraStopCard";
import { WorkflowDialog } from "@/components/WorkflowDialog";
import { fmtTime12 } from "@/lib/scheduleWindows";
import { CheckCircle, Phone } from "@phosphor-icons/react";
import { crewLabel } from "@/lib/permissions";
import LabelDialog from "@/components/LabelDialog";
import { useDirectory } from "@/lib/directory";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { JobFieldRecords } from "@/components/JobFieldRecords";
import { ApprovalTrail } from "@/components/ApprovalTrail";
import { ReservedPieces } from "@/components/ReservedPieces";
import { money } from "@/lib/format";
import { DesignerPlan } from "@/components/CountertopDesigner";

const LINK_PATH = {
  hold: (id) => `/reservation/${id}`,
  slab: (id) => `/inventory/slabs/${id}`,
  piece: (id) => `/inventory/slabs/${String(id).split(":")[0]}`,
  remnant: (id) => `/inventory/remnants/${id}`,
};

const TABS = [
  { key: "overview", label: "Overview", icon: SquaresFour },
  { key: "schedule", label: "Tasks", icon: CalendarBlank },
  { key: "materials", label: "Materials", icon: Stack },
  { key: "comms", label: "Comms", icon: ChatCircle },
  { key: "files", label: "Files", icon: Images },
];

const SectionCard = ({ title, icon, action, children, testId }) => (
  <div
    className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
    data-testid={testId}
  >
    <div className="flex items-center gap-2">
      {icon}
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const jobMoney = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const ProfitCard = ({ jobId }) => {
  const [data, setData] = useState(null);
  const [costs, setCosts] = useState({
    cost_material: "",
    cost_labor: "",
    cost_other: "",
  });
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [scan, setScan] = useState(null);
  const invoiceRef = useRef(null);

  const readInvoice = async (file) => {
    if (!file) return;
    setReading(true);
    setScan(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: entry } = await api.post(`/jobs/${jobId}/cost-invoice`, form);
      setScan(entry);
      setData((d) => (d ? { ...d, cost_invoices: [...(d.cost_invoices || []), entry] } : d));
      if (entry.read && entry.material_total > 0) {
        toast.success(`Read ${entry.supplier || "supplier"} invoice — ${jobMoney(entry.material_total)}`);
      } else {
        toast("Invoice saved on the project", {
          description: "The total couldn't be read automatically — type the material cost in.",
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not upload that invoice");
    } finally {
      setReading(false);
      if (invoiceRef.current) invoiceRef.current.value = "";
    }
  };

  const applyScan = (mode) => {
    if (!scan?.material_total) return;
    setCosts((c) => ({
      ...c,
      cost_material:
        mode === "add" ? Math.round((num(c.cost_material) + scan.material_total) * 100) / 100 : scan.material_total,
    }));
    toast.success(`${mode === "add" ? "Added" : "Set"} material cost ${jobMoney(scan.material_total)} — remember to save`);
  };

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/jobs/${jobId}/profit`);
    setData(d);
    setCosts({
      cost_material: d.cost_material || "",
      cost_labor: d.cost_labor || "",
      cost_other: d.cost_other || "",
    });
  }, [jobId]);

  useEffect(() => {
    load()
      .then(() => {
        if (window.location.hash === "#costs") {
          setTimeout(() => document.getElementById("costs")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
        }
      })
      .catch(() => {});
  }, [load]);

  const num = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
  const total = num(costs.cost_material) + num(costs.cost_labor) + num(costs.cost_other);
  const invoiced = Number(data?.invoiced || 0);
  const margin = invoiced - total;
  const marginPct = invoiced ? (margin / invoiced) * 100 : null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}`, {
        cost_material: num(costs.cost_material),
        cost_labor: num(costs.cost_labor),
        cost_other: num(costs.cost_other),
      });
      toast.success("Costs saved");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save costs",
      );
    }
    setSaving(false);
  };

  const autoFill = () => {
    const s = data?.material_suggestion;
    if (!s?.amount) return;
    setCosts((c) => ({ ...c, cost_material: s.amount }));
    toast.success(
      `Material cost filled from ${s.slab_color} (${s.sqft} ft² × ${jobMoney(s.cost_per_sqft)})`,
    );
  };

  const fillFromLines = () => {
    const lc = data?.line_costs;
    if (!lc?.cost_total) return;
    setCosts((c) => ({
      ...c,
      cost_material: lc.cost_material || c.cost_material,
      cost_other: lc.cost_other || c.cost_other,
    }));
    toast.success(
      `Filled from ${lc.lines_priced} priced quote line${lc.lines_priced === 1 ? "" : "s"}`,
    );
  };

  const autoFillLabor = () => {
    const s = data?.material_suggestion;
    if (!s?.labor_amount) return;
    setCosts((c) => ({ ...c, cost_labor: s.labor_amount }));
    toast.success(`Labor cost filled: ${s.sqft} ft² × ${jobMoney(s.labor_rate)}/ft²`);
  };

  if (!data) return null;
  const noCosts = !num(data.cost_material) && !num(data.cost_labor) && !num(data.cost_other);

  const field = (key, label) => (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </span>
      <div className="flex items-center rounded-lg border border-zinc-800 bg-zinc-950 px-2">
        <span className="text-sm text-zinc-500">$</span>
        <input
          type="number"
          min="0"
          step="0.01"
          data-testid={`job-profit-${key}`}
          value={costs[key]}
          onChange={(e) => setCosts((c) => ({ ...c, [key]: e.target.value }))}
          className="w-full bg-transparent px-1 py-2 text-sm text-zinc-100 outline-none"
        />
      </div>
    </label>
  );

  return (
    <div
      id="costs"
      className={`rounded-lg border bg-zinc-900 p-4 ${noCosts ? "border-amber-500/40" : "border-zinc-800"}`}
      data-testid="job-profit-card"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Costs &amp; margin
        </div>
        <span
          data-testid="job-profit-margin"
          className={`rounded-full px-3 py-1 text-xs font-bold ${
            noCosts
              ? "bg-amber-500/10 text-amber-300"
              : margin >= 0
                ? "bg-emerald-500/10 text-emerald-300"
                : "bg-red-500/10 text-red-300"
          }`}
        >
          {noCosts ? "No costs yet" : jobMoney(margin)}
          {!noCosts && marginPct != null ? ` · ${marginPct.toFixed(1)}%` : ""}
        </span>
      </div>
      {noCosts && (
        <p className="mt-2 text-xs text-amber-200/90" data-testid="job-profit-empty">
          Enter what this job cost in material and labor — the Margin report treats jobs
          without costs as 100% margin until you do.
        </p>
      )}
      {!noCosts && data.costs_entered_at && (
        <p className="mt-2 text-[11px] text-zinc-500" data-testid="job-profit-stamp">
          Costs entered {data.costs_entered_at.slice(0, 10)}
          {data.costs_entered_by ? ` by ${data.costs_entered_by}` : ""}
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Invoiced
          </div>
          <div className="mt-1 font-semibold text-zinc-100" data-testid="job-profit-invoiced">
            {jobMoney(data.invoiced)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Collected
          </div>
          <div className="mt-1 font-semibold text-zinc-100" data-testid="job-profit-paid">
            {jobMoney(data.paid)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Total cost
          </div>
          <div
            className="mt-1 font-semibold text-zinc-100"
            data-testid="job-profit-cost-total"
          >
            {jobMoney(total)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Margin
          </div>
          <div
            className={`mt-1 font-semibold ${
              margin >= 0 ? "text-emerald-300" : "text-red-300"
            }`}
          >
            {jobMoney(margin)}
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {field("cost_material", "Material $")}
        {field("cost_labor", "Labor $")}
        {field("cost_other", "Other $")}
      </div>
      {data.line_costs?.cost_total > 0 && (
        <div
          className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200"
          data-testid="job-line-costs"
        >
          <span>
            Quote lines carry {jobMoney(data.line_costs.cost_total)} of cost
            ({data.line_costs.lines_priced} priced line
            {data.line_costs.lines_priced === 1 ? "" : "s"})
          </span>
          <button
            type="button"
            data-testid="job-fill-from-lines"
            onClick={fillFromLines}
            className="rounded-full border border-emerald-400/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 hover:bg-emerald-500/20"
          >
            Use these costs
          </button>
        </div>
      )}
      <div
        className="mt-3 rounded-lg border border-dashed border-zinc-700 bg-zinc-950/50 p-3"
        data-testid="job-cost-invoice"
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="job-cost-invoice-upload"
            disabled={reading}
            onClick={() => invoiceRef.current?.click()}
            className="flex items-center gap-2 rounded-lg border border-zinc-700 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-200 transition-colors hover:border-yellow-500/50 disabled:opacity-50"
          >
            <Images size={14} weight="bold" />
            {reading ? "Reading invoice…" : "Snap / upload supplier invoice"}
          </button>
          <input
            ref={invoiceRef}
            type="file"
            accept="image/*,application/pdf,.heic"
            capture="environment"
            className="hidden"
            data-testid="job-cost-invoice-input"
            onChange={(e) => readInvoice(e.target.files?.[0])}
          />
          <span className="text-[11px] text-zinc-500">
            Photo or PDF — the total is read for you and kept on the project.
          </span>
        </div>
        {scan && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="job-cost-invoice-result">
            <span className="text-zinc-300">
              {scan.supplier || "Supplier"}
              {scan.invoice_number ? ` · #${scan.invoice_number}` : ""}
              {scan.date ? ` · ${scan.date}` : ""}
            </span>
            <span className="font-mono-data font-bold text-yellow-400" data-testid="job-cost-invoice-total">
              {jobMoney(scan.material_total || scan.total || 0)}
            </span>
            {scan.material_total > 0 && (
              <>
                <button
                  type="button"
                  data-testid="job-cost-invoice-use"
                  onClick={() => applyScan("set")}
                  className="rounded-lg bg-yellow-500 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-950 hover:bg-yellow-400"
                >
                  Use as material
                </button>
                <button
                  type="button"
                  data-testid="job-cost-invoice-add"
                  onClick={() => applyScan("add")}
                  className="rounded-lg border border-zinc-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-200 hover:border-zinc-500"
                >
                  Add to material
                </button>
              </>
            )}
          </div>
        )}
        {(data.cost_invoices || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-zinc-400" data-testid="job-cost-invoice-list">
            {data.cost_invoices.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3">
                <a
                  href={`${API}/jobs/${jobId}/cost-invoices/${e.id}/file`}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate hover:text-yellow-400 hover:underline"
                >
                  {e.supplier || e.file_name || "Invoice"}
                  {e.invoice_number ? ` #${e.invoice_number}` : ""} · {(e.at || "").slice(0, 10)}
                </a>
                <span className="font-mono-data shrink-0 text-zinc-300">{jobMoney(e.material_total || e.total || 0)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {data.material_suggestion?.amount > 0 && (
          <button
            type="button"
            data-testid="job-profit-autofill"
            onClick={autoFill}
            className="rounded-lg border border-indigo-500/30 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-indigo-300 transition-colors hover:bg-indigo-500/10"
          >
            Auto-fill material — {jobMoney(data.material_suggestion.amount)}
          </button>
        )}
        {data.material_suggestion?.labor_amount > 0 && (
          <button
            type="button"
            data-testid="job-profit-autofill-labor"
            onClick={autoFillLabor}
            className="rounded-lg border border-sky-500/30 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-sky-300 transition-colors hover:bg-sky-500/10"
          >
            Auto-fill labor — {jobMoney(data.material_suggestion.labor_amount)}
          </button>
        )}
        <Button
          data-testid="job-profit-save"
          onClick={save}
          disabled={saving}
          className="ml-auto gap-2 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save costs"}
        </Button>
      </div>
      {data.material_suggestion && (
        <p className="mt-2 text-[11px] text-zinc-500">
          {data.material_suggestion.amount > 0 && (
            <>
              Material from {data.material_suggestion.slab_color}: {data.material_suggestion.sqft} ft² ×{" "}
              {jobMoney(data.material_suggestion.cost_per_sqft)}/ft².{" "}
            </>
          )}
          {data.material_suggestion.labor_amount > 0 ? (
            <>
              Labor at the shop rate of {jobMoney(data.material_suggestion.labor_rate)}/ft² (Settings → Pricing).
            </>
          ) : (
            <>
              Set a shop labor cost $/ft² in Settings → Pricing to get a labor estimate too.
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default function JobDetail({ embedded = false, jobId = "" }) {
  const { id: routeId } = useParams();
  const id = jobId || routeId;
  const Wrap = embedded ? "div" : "main";
  const { nameOf } = useDirectory();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [reservedPieces, setReservedPieces] = useState([]);
  const [note, setNote] = useState("");
  const [msgTab, setMsgTab] = useState("text");
  const [jobPos, setJobPos] = useState([]);
  const [drawingApproval, setDrawingApproval] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [linkedQuote, setLinkedQuote] = useState(undefined);
  const [filesTab, setFilesTab] = useState("photos");
  const [tab, setTab] = useState(
    TABS.some((t) => t.key === searchParams.get("tab"))
      ? searchParams.get("tab")
      : "overview",
  );
  const swipe = useSwipeTabs(TABS, tab, (t) => setTab(t.key));

  const [panel, setPanel] = useState(null);
  const [draft, setDraft] = useState({});
  const [taskDialog, setTaskDialog] = useState({ open: false, task: null });
  const [taskTypes, setTaskTypes] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [workflowFocus, setWorkflowFocus] = useState("");
  const [apptBusy, setApptBusy] = useState("");
  const [labelDlg, setLabelDlg] = useState(null);
  const [apptLog, setApptLog] = useState(null);
  const [apptConfirm, setApptConfirm] = useState(null);
  const [apptEmail, setApptEmail] = useState(null);

  useEffect(() => {
    if (!job?.id) return;
    api
      .get(`/jobs/${job.id}/appointment-log`)
      .then(({ data }) => setApptLog(data))
      .catch(() => {});
  }, [job?.id, job?.appt_confirmed_at]);

  const openAddTask = () => setTaskDialog({ open: true, task: null });
  const openEditTask = (st) => setTaskDialog({ open: true, task: st });
  const deleteStop = async (sid) => {
    try {
      const { data } = await api.delete(`/jobs/${id}/extra-stops/${sid}`);
      setJob(data);
      toast.success("Task removed");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not remove",
      );
    }
  };

  const startEdit = (name, fields) => {
    setPanel(name);
    setDraft(fields);
  };

  const savePanel = async () => {
    try {
      const { data } = await api.patch(`/jobs/${id}`, draft);
      setJob(data);
      setPanel(null);
      toast.success("Job updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
  };

  const editBtn = (name, fields) =>
    panel === name ? (
      <span className="ml-auto flex gap-2">
        <button
          data-testid={`panel-save-${name}`}
          onClick={savePanel}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-indigo-500 bg-indigo-500 px-3 text-[11px] font-bold uppercase tracking-widest text-white"
        >
          <Check size={12} weight="bold" /> Save
        </button>
        <button
          data-testid={`panel-cancel-${name}`}
          onClick={() => setPanel(null)}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-zinc-700 px-3 text-[11px] uppercase tracking-widest text-zinc-400 hover:bg-zinc-800"
        >
          <X size={12} weight="bold" />
        </button>
      </span>
    ) : (
      <button
        data-testid={`panel-edit-${name}`}
        onClick={() => startEdit(name, fields)}
        title="Edit this panel"
        className="ml-auto text-zinc-500 transition-colors duration-150 hover:text-yellow-400"
      >
        <PencilSimple size={15} weight="bold" />
      </button>
    );

  const load = useCallback(
    () => api.get(`/jobs/${id}`).then(({ data }) => setJob(data)),
    [id],
  );

  useEffect(() => {
    load().catch(() => toast.error("Job not found"));
  }, [load]);

  useEffect(() => {
    api
      .get("/settings/task-types")
      .then(({ data }) => setTaskTypes(data.task_types || []))
      .catch(() => {});
    api
      .get("/crew-list")
      .then(({ data }) => setCrewList(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/purchase-orders", { params: { job_id: id } })
      .then(({ data }) => setJobPos(data))
      .catch(() => setJobPos([]));
  }, [id]);

  useEffect(() => {
    api
      .get(`/jobs/${id}/pieces`)
      .then(({ data }) => setReservedPieces(data.items || []))
      .catch(() => setReservedPieces([]));
  }, [id, job?.updated_at]);

  useEffect(() => {
    api
      .get(`/jobs/${id}/drawing-approval`)
      .then(({ data }) => setDrawingApproval(data))
      .catch(() => setDrawingApproval(null));
  }, [id, job?.updated_at]);

  useEffect(() => {
    if (!job?.quote_id) {
      setLinkedQuote(null);
      return;
    }
    api
      .get(`/quotes/${job.quote_id}`)
      .then(({ data }) => setLinkedQuote(data))
      .catch(() => setLinkedQuote(null));
  }, [job?.quote_id, job?.updated_at]);

  useEffect(() => {
    if (!job?.customer_id) {
      setCustomer(null);
      return;
    }
    api
      .get(`/customers/${job.customer_id}`)
      .then(({ data }) => setCustomer(data))
      .catch(() => setCustomer(null));
  }, [job?.customer_id]);

  const setStage = async (stage) => {
    try {
      const { data } = await api.patch(`/jobs/${id}/stage`, { stage });
      setJob(data);
      const review = data.review_request?.to;
      toast.success(
        `Moved to ${stage}`,
        stage === "Complete" && review
          ? { description: `"How did we do?" review request emailed to ${review}` }
          : undefined,
      );
      if (stage === "Complete") {
        const hasCosts = (data.cost_material || 0) + (data.cost_labor || 0) + (data.cost_other || 0) > 0;
        if (!hasCosts) {
          toast("Enter this job's costs", {
            id: `costs-${id}`,
            duration: 12000,
            description: "Material and labor are still blank — the Margin report counts it as 100% until you fill them in.",
            action: {
              label: "Enter costs",
              onClick: () => {
                setTab("overview");
                setTimeout(
                  () => document.getElementById("costs")?.scrollIntoView({ behavior: "smooth", block: "center" }),
                  120,
                );
              },
            },
          });
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    const { data } = await api.post(`/jobs/${id}/notes`, { body: note });
    setJob(data);
    setNote("");
    toast.success("Note added");
  };

  const remove = async () => {
    await api.delete(`/jobs/${id}`);
    toast.success("Job deleted");
    navigate("/projects");
  };

  const createQuote = async () => {
    try {
      const { data } = await api.post(`/jobs/${id}/create-quote`);
      toast.success(`Quote ${data.quote_code || ""} created`);
      navigate(`/quotes/${data.quote_id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create a quote",
      );
    }
  };

  if (!job)
    return (
      <div className={embedded ? "" : "grain min-h-screen"}>
        {!embedded && <AppHeader title="Job" />}
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
        </div>
      </div>
    );

  const idx = JOB_STAGES.indexOf(job.stage);
  const nextStage =
    idx >= 0 && idx < JOB_STAGES.length - 1 ? JOB_STAGES[idx + 1] : null;
  const scheduledStops = [
    ["Template", job.template_date],
    ["Fabrication", job.fab_date],
    ["Install", job.install_date],
  ].filter(([, d]) => !!d);
  const fieldStopStatuses = [
    ...scheduledStops.map(([k]) => job.stop_status?.[k]?.status || ""),
    ...(job.extra_stops || []).map((s) => s.stop_status?.status || ""),
  ];
  const readyToComplete =
    job.stage !== "Complete" &&
    !!job.install_date &&
    job.stop_status?.Install?.status === "Done" &&
    fieldStopStatuses.length > 0 &&
    fieldStopStatuses.every((s) => s === "Done");
  const companyName = (customer?.company || "").trim();
  const contactObj = (customer?.contacts || []).find((c) => (c.name || "").trim());
  const contactName =
    (contactObj?.name || "").trim() || (job.customer_name || "").trim();
  const contactPhone = (contactObj?.phone || customer?.phone || "").trim();
  const schedule = [
    [
      "Template",
      job.template_date,
      job.template_window,
      job.template_crew,
      job.template_time,
    ],
    ["Fabrication", job.fab_date, job.fab_window, job.fab_crew, job.fab_time],
    [
      "Install",
      job.install_date,
      job.install_window,
      job.install_crew,
      job.install_time,
    ],
  ];
  const notes = [...(job.notes || [])].reverse();

  // ---- section blocks (composed into tabs below) ----

  const addressCard = (
    <SectionCard
      testId="job-address-card"
      icon={<MapPin size={16} weight="bold" className="text-yellow-500" />}
      title="Job Address"
      action={editBtn("address", { address: job.address || "" })}
    >
      {panel === "address" ? (
        <AddressCombo
          testId="panel-address"
          className="mt-2"
          value={draft.address}
          onChange={(v) => setDraft({ address: v })}
          options={[customer?.address, ...(customer?.job_addresses || [])]}
        />
      ) : (
        <div
          className="mt-2 text-lg font-semibold"
          data-testid="job-address-value"
        >
          {job.address || "—"}
        </div>
      )}
      {job.address && (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="job-address-directions"
          className="mt-2 inline-block text-xs uppercase tracking-widest text-yellow-400 hover:underline"
        >
          Get directions
        </a>
      )}
    </SectionCard>
  );

  const openApptText = async (kind, stopId = "") => {
    setApptBusy(stopId || kind);
    try {
      const { data } = await api.get(
        `/jobs/${job.id}/appointment-text/preview`,
        { params: { kind, stop_id: stopId } },
      );
      setApptConfirm({ kind, stopId, to: data.to, message: data.message });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load the appointment text");
    } finally {
      setApptBusy("");
    }
  };

  const doSendApptText = async (message) => {
    const kind = apptConfirm?.kind;
    if (!kind) return;
    setApptBusy(apptConfirm.stopId || kind);
    try {
      const { data } = await api.post(`/jobs/${job.id}/appointment-text`, {
        kind,
        message,
        stop_id: apptConfirm.stopId || "",
      });
      toast.success(`Appointment text sent to ${data.to}`);
      setApptConfirm(null);
      api
        .get(`/jobs/${job.id}/appointment-log`)
        .then(({ data: d }) => setApptLog(d))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the appointment text");
    } finally {
      setApptBusy("");
    }
  };

  const openApptEmail = async (kind, stopId = "") => {
    setApptBusy(`email-${stopId || kind}`);
    try {
      const { data } = await api.get(`/jobs/${job.id}/appointment-email/preview`, {
        params: { kind, stop_id: stopId },
      });
      setApptEmail({ kind, stopId, to: data.to, message: data.message });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load the appointment email");
    } finally {
      setApptBusy("");
    }
  };

  const doSendApptEmail = async (message) => {
    const kind = apptEmail?.kind;
    if (!kind) return;
    setApptBusy(`email-${apptEmail.stopId || kind}`);
    try {
      const { data } = await api.post(`/jobs/${job.id}/appointment-email`, {
        kind,
        message,
        stop_id: apptEmail.stopId || "",
      });
      toast.success(`Appointment email sent to ${data.to}`);
      setApptEmail(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the appointment email");
    } finally {
      setApptBusy("");
    }
  };

  const scheduleCard = (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-schedule"
    >
      {job.appt_confirmed_at && (
        <div
          data-testid="appt-confirmed-badge"
          className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300"
        >
          <CheckCircle size={12} weight="fill" /> Customer confirmed
        </div>
      )}
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/15 text-indigo-300">
          <CalendarBlank size={16} weight="bold" />
        </span>
        <div className="text-sm font-bold uppercase tracking-widest text-zinc-100">
          Workflow
        </div>
        <button
          type="button"
          data-testid="job-workflow-open"
          onClick={() => setWorkflowOpen(true)}
          className="ml-auto inline-flex h-8 items-center gap-1 rounded-md bg-indigo-500 px-3 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
        >
          <PencilSimple size={12} weight="bold" /> Edit
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {schedule.map(([label, date, window, crew, time]) => (
          <div
            key={label}
            data-testid={`schedule-${label}`}
            className="flex flex-wrap items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
          >
            <span className="w-24 text-xs uppercase tracking-widest text-zinc-400">
              {label}
            </span>
            <span className="font-mono-data inline-flex items-center gap-1 text-sm">
              <CalendarBlank size={14} /> {date || "Not scheduled"}
            </span>{" "}
            {date && (
              <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300">
                {time ? `${fmtTime12(time)} · ${window}` : window}
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400">
              <User size={13} /> {crewLabel(crew)}
            </span>
            {date && (
              <button
                type="button"
                data-testid={`appt-text-${label}`}
                onClick={() => openApptText(label)}
                disabled={apptBusy === label}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-indigo-500/50 bg-indigo-500/10 px-2 text-[10px] font-bold uppercase tracking-widest text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20 disabled:opacity-50"
              >
                <ChatCircle size={12} weight="bold" />{" "}
                {apptBusy === label ? "Sending\u2026" : "Text appt"}
              </button>
            )}
            {date && (
              <button
                type="button"
                data-testid={`appt-email-${label}`}
                onClick={() => openApptEmail(label)}
                disabled={apptBusy === `email-${label}`}
                className="inline-flex h-7 items-center gap-1 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-2 text-[10px] font-bold uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/20 disabled:opacity-50"
              >
                <EnvelopeSimple size={12} weight="bold" />{" "}
                {apptBusy === `email-${label}` ? "Sending\u2026" : "Email appt"}
              </button>
            )}
          </div>
        ))}
      </div>
      <ConfirmTextDialog
        open={!!apptConfirm}
        onOpenChange={(o) => !o && setApptConfirm(null)}
        to={apptConfirm?.to}
        firstName={(job.customer_name || "").split(" ")[0]}
        message={apptConfirm?.message || ""}
        title="Appointment text — review"
        sending={!!apptBusy}
        onSend={doSendApptText}
      />
      <ConfirmTextDialog
        open={!!apptEmail}
        onOpenChange={(o) => !o && setApptEmail(null)}
        to={apptEmail?.to}
        firstName={(job.customer_name || "").split(" ")[0]}
        message={apptEmail?.message || ""}
        title="Appointment email — review"
        sendLabel="Send email"
        showPresets={false}
        sending={!!apptBusy}
        onSend={doSendApptEmail}
      />
      {apptLog && (apptLog.items.length > 0 || apptLog.confirmed_at) && (
        <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="appt-log">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
            Customer texts
          </div>
          <div className="space-y-1.5">
            {apptLog.items.map((it, i) => (
              <div
                key={i}
                data-testid="appt-log-item"
                className="flex items-start gap-2 text-xs text-zinc-400"
              >
                <span
                  className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
                    it.kind === "Update"
                      ? "bg-amber-500/15 text-amber-300"
                      : it.kind === "Reminder"
                        ? "bg-sky-500/15 text-sky-300"
                        : "bg-zinc-700/40 text-zinc-300"
                  }`}
                >
                  {it.kind}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-zinc-300">{it.body}</span>
                  <span className="text-[10px] text-zinc-600">
                    {it.at ? new Date(it.at).toLocaleString() : ""}
                    {it.status ? ` · ${it.status}` : ""}
                  </span>
                </span>
              </div>
            ))}
            {apptLog.confirmed_at && (
              <div
                data-testid="appt-log-confirmed"
                className="flex items-start gap-2 text-xs"
              >
                <span className="mt-0.5 shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300">
                  Confirmed
                </span>
                <span className="min-w-0">
                  <span className="block text-emerald-300">
                    Confirmed by customer
                    {apptLog.confirmed_via === "portal"
                      ? " on the customer portal"
                      : apptLog.confirmed_via === "text"
                        ? " by text reply"
                        : ""}
                  </span>
                  <span className="text-[10px] text-zinc-600">
                    {new Date(apptLog.confirmed_at).toLocaleString()}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className="mt-3 border-t border-zinc-800 pt-3"
        data-testid="job-extra-stops"
      >
        <div className="space-y-2">
          {(job.extra_stops || []).map((st) => (
            <ExtraStopCard
              key={st.id}
              jobId={job.id}
              stop={st}
              onChanged={(data) => setJob(data)}
              onEdit={openEditTask}
              onDelete={deleteStop}
              onTextAppt={(s) => openApptText(s.kind, s.id)}
              apptBusy={apptBusy === st.id}
            />
          ))}
        </div>
        <button
          type="button"
          data-testid="extra-task-add"
          onClick={openAddTask}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-zinc-700 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-500/50 hover:text-indigo-300"
        >
          <Plus size={14} weight="bold" /> Add stage (template / fab / install)
        </button>
      </div>
      <TaskDialog
        open={taskDialog.open}
        onOpenChange={(v) => setTaskDialog((p) => ({ ...p, open: v }))}
        resourcePath={`/jobs/${id}/extra-stops`}
        areasSource={`/jobs/${id}/drawing-areas`}
        task={taskDialog.task}
        taskTypes={taskTypes}
        crewList={crewList}
        noun="stage"
        onSaved={(data) => setJob(data)}
      />
    </div>
  );

  const reservedCard = reservedPieces.length > 0 && (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-reserved-pieces"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Reserved Slabs ({reservedPieces.length})
        </div>
        <div className="text-[11px] text-zinc-500">
          Pull by serial number — the photo is of the exact piece when tagged “This piece”.
        </div>
      </div>
      <div className="mt-3">
        <ReservedPieces pieces={reservedPieces} />
      </div>
    </div>
  );

  const sinkCard = (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <div className="flex items-center gap-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Sink
        </div>
        {editBtn("sink", {
          sink_location: job.sink_location || "Warehouse",
          sink_notes: job.sink_notes || "",
        })}
      </div>
      {panel === "sink" ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="panel-sink-edit">
          <Input
            data-testid="panel-sink-location"
            value={draft.sink_location}
            onChange={(e) =>
              setDraft({ ...draft, sink_location: e.target.value })
            }
            placeholder="Warehouse or On-Site"
            className="h-12 border-zinc-800 bg-zinc-950"
          />
          <Input
            data-testid="panel-sink-notes"
            value={draft.sink_notes}
            onChange={(e) => setDraft({ ...draft, sink_notes: e.target.value })}
            placeholder="Model # / description"
            className="h-12 border-zinc-800 bg-zinc-950"
          />
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span data-testid="job-sink-location">
            <span className="text-xs uppercase tracking-widest text-zinc-500">
              Location{" "}
            </span>
            {job.sink_location || "—"}
          </span>
          <span data-testid="job-sink-notes">
            <span className="text-xs uppercase tracking-widest text-zinc-500">
              Notes{" "}
            </span>
            {job.sink_notes || "—"}
          </span>
        </div>
      )}
      <div className="mt-4 border-t border-zinc-800 pt-3 [&>div]:border-0 [&>div]:bg-transparent [&>div]:p-0">
        <SinkAssign
          entity="job"
          entityId={id}
          customerName={job.customer_name}
          projectName={job.project_name}
        />
      </div>
    </div>
  );

  const poCard = jobPos.length > 0 && (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-purchase-orders"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        Material On Order ({jobPos.length})
      </div>
      <div className="mt-3 space-y-2">
        {jobPos.map((po) => (
          <Link
            key={po.id}
            to={`/inventory/purchase-orders/${po.id}`}
            data-testid={`job-po-${po.id}`}
            className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3 transition-colors duration-150 hover:border-indigo-500/40"
          >
            <div className="min-w-0">
              <div className="font-mono-data text-xs text-indigo-300">
                {po.code}
              </div>
              <div className="truncate text-sm font-semibold">
                {po.supplier_name || "No supplier"}
              </div>
              <div className="text-xs text-zinc-500">
                {po.lines
                  .map((l) => l.slab_color || l.item_name)
                  .filter(Boolean)
                  .join(", ") || `${po.lines.length} lines`}
                {po.expected_date ? ` · due ${po.expected_date}` : ""}
              </div>
            </div>
            <span className="shrink-0 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300">
              {po.status}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );

  const messagesCard = (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-messages-card"
    >
      <div className="flex overflow-hidden rounded-md border border-zinc-800">
        {[
          ["text", "Texts"],
          ["emails", "Emails"],
          ["follow", "Follow-ups"],
          ["notes", "Team Notes"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`job-messages-tab-${k}`}
            onClick={() => setMsgTab(k)}
            className={`h-11 flex-1 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
              msgTab === k
                ? "bg-indigo-500 text-white"
                : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {msgTab === "text" ? (
        <div className="mt-3 [&>div]:border-0 [&>div]:bg-transparent [&>div]:p-0">
          <SmsPanel
            entity="job"
            entityId={id}
            presets={[
              {
                label: "Schedule",
                text: `Hi, this is Brioni Granite about ${job.project_name}. Confirming your scheduled visit — reply here if the time needs to change.`,
              },
              {
                label: "On the way",
                text: "Hi, our crew is on the way to you now. See you shortly — Brioni Granite.",
              },
            ]}
          />
        </div>
      ) : msgTab === "emails" ? (
        <JobEmailThread jobIds={[id]} composeJobId={id} testId="job-email-thread" />
      ) : msgTab === "follow" ? (
        <div className="mt-3" data-testid="job-follow-tab">
          <div data-testid="job-follow-up">
            <div className="flex items-center justify-end gap-2">
              {editBtn("follow", {
                follow_up_date: job.follow_up_date || "",
                follow_up_note: job.follow_up_note || "",
              })}
            </div>
            {panel === "follow" ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <DateField
                  testId="panel-follow-date"
                  value={draft.follow_up_date}
                  onChange={(v) => setDraft({ ...draft, follow_up_date: v })}
                />
                <Input
                  data-testid="panel-follow-note"
                  value={draft.follow_up_note}
                  onChange={(e) =>
                    setDraft({ ...draft, follow_up_note: e.target.value })
                  }
                  placeholder="Call about edge profile"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </div>
            ) : job.follow_up_date ? (
              <div className="mt-2 text-sm" data-testid="job-follow-up-value">
                <span
                  className={`font-mono-data font-bold ${
                    job.follow_up_date <= new Date().toISOString().slice(0, 10)
                      ? "text-red-400"
                      : "text-yellow-400"
                  }`}
                >
                  {job.follow_up_date}
                </span>
                {job.follow_up_note && (
                  <span className="ml-3 text-zinc-300">
                    {job.follow_up_note}
                  </span>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-zinc-500">No follow-up set.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3" data-testid="job-notes-tab">
          <form onSubmit={addNote} data-testid="job-note-form">
            <Textarea
              data-testid="job-note-input"
              required
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Shop-only note — crew instructions, issues, follow-ups…"
              className="mt-3 border-zinc-800 bg-zinc-950"
            />
            <Button
              data-testid="add-job-note-button"
              type="submit"
              variant="outline"
              className="mt-3 h-12 w-full border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
            >
              Add Note
            </Button>
            <div className="mt-4 space-y-3" data-testid="job-notes">
              {notes.length === 0 && (
                <p className="text-sm text-zinc-500">No internal notes yet.</p>
              )}
              {notes.map((n) => (
                <div
                  key={n.id}
                  data-testid={`job-note-${n.id}`}
                  className="relative rounded-lg border border-zinc-800 bg-zinc-950 p-3 pl-4 transition-colors duration-150 hover:border-indigo-500/40"
                >
                  <p className="whitespace-pre-wrap text-sm text-zinc-200">
                    {n.body}
                  </p>
                  <div className="font-mono-data mt-1 text-xs text-zinc-600">
                    {(n.created_at || "").slice(0, 16).replace("T", " ")} ·{" "}
                    {nameOf(n.created_by)}
                  </div>
                </div>
              ))}
            </div>
          </form>
        </div>
      )}
    </div>
  );

  const stageHistoryCard = (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        Stage History
      </div>
      {job.review_request?.to && (
        <a
          href={job.review_request.url}
          target="_blank"
          rel="noreferrer"
          data-testid="job-review-request"
          title="Open the review link the customer received"
          className="mt-2 flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 transition-colors duration-150 hover:border-amber-400/60"
        >
          <span className="min-w-0 truncate">
            <span className="font-semibold uppercase tracking-wider">Review request</span> · emailed to{" "}
            {job.review_request.to}
            {job.review_request.clicked_at
              ? " · link opened"
              : job.review_request.reminder_at
                ? " · reminder sent"
                : ""}
          </span>
          <span className="font-mono-data shrink-0 text-amber-200/70">
            {(job.review_request.at || "").slice(0, 10)}
          </span>
        </a>
      )}
      <div className="mt-2" data-testid="job-stage-history">
        {[...(job.stage_history || [])].reverse().map((h, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-3 border-b border-zinc-800/50 py-2 text-sm last:border-0"
          >
            <span className="flex items-center gap-2.5">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${STAGE_DOT[h.stage] || STAGE_DOT.Quote}`}
              />
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">
                {h.stage}
              </span>
            </span>
            <span className="font-mono-data text-xs text-zinc-500">
              {(h.at || "").slice(0, 16).replace("T", " ")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const projectCard = null;

  const summaryCard = (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-summary-card"
    >
      <div className="flex items-center gap-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Job Summary
        </div>
        {editBtn("summary", { summary: job.summary || "" })}
      </div>
      {panel === "summary" ? (
        <Textarea
          data-testid="panel-summary-input"
          value={draft.summary}
          onChange={(e) => setDraft({ summary: e.target.value })}
          rows={4}
          className="mt-3 border-zinc-800 bg-zinc-950"
        />
      ) : (
        <p
          className="mt-3 whitespace-pre-wrap text-sm text-zinc-200"
          data-testid="job-summary"
        >
          {job.summary || "—"}
        </p>
      )}
      <div className="mt-4 flex items-center gap-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Gate / Entry Notes
        </div>
        {editBtn("access_notes", { access_notes: job.access_notes || "" })}
      </div>
      {panel === "access_notes" ? (
        <Textarea
          data-testid="panel-access-notes-input"
          value={draft.access_notes}
          onChange={(e) => setDraft({ access_notes: e.target.value })}
          rows={2}
          placeholder="Gate code, lockbox, side door, dog in yard…"
          className="mt-3 border-zinc-800 bg-zinc-950"
        />
      ) : (
        <p
          className="mt-2 whitespace-pre-wrap text-sm text-zinc-200"
          data-testid="job-access-notes"
        >
          {job.access_notes || "—"}
        </p>
      )}
    </div>
  );

  const materialCard = (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-material-card"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        Linked Material
      </div>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="job-material-links">
        {(job.material_links || []).length === 0 && (
          <span className="text-sm text-zinc-500">No material linked yet.</span>
        )}
        {(job.material_links || []).map((l) => (
          <Link
            key={`${l.type}:${l.id}`}
            to={LINK_PATH[l.type](l.id)}
            data-testid={`job-material-${l.id}`}
            className="rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs transition-colors duration-150 hover:border-indigo-500"
          >
            <span className="font-mono-data text-indigo-300">{l.code}</span>{" "}
            <span className="text-zinc-300">{l.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );

  const quoteAreas = (linkedQuote?.areas || []).map((a) => ({
    ...a,
    pieces: (a.pieces || []).filter(
      (p) => !p.panel_kind && (p.points || []).length >= 3,
    ),
  }));
  const quoteCard = job.quote_id ? (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-quote-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Receipt size={16} weight="bold" className="text-indigo-300" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Quote &amp; drawing
            </div>
            <div
              className="text-sm font-bold text-zinc-100"
              data-testid="job-quote-code"
            >
              {linkedQuote?.code || job.quote_code || "Linked quote"}
              {linkedQuote?.totals?.total != null && (
                <span className="font-mono-data ml-2 text-indigo-300">
                  {money(linkedQuote.totals.total)}
                </span>
              )}
              {linkedQuote?.status && (
                <span className="ml-2 text-[11px] font-normal capitalize text-zinc-500">
                  {linkedQuote.status}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/quotes/${job.quote_id}`}
            data-testid="job-quote-open"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:border-indigo-400/50 hover:text-white"
          >
            Open quote
          </Link>
          <Link
            to={`/quotes/${job.quote_id}/design`}
            data-testid="job-quote-design"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-200 transition-colors duration-150 hover:border-indigo-400/50 hover:text-white"
          >
            Design studio
          </Link>
        </div>
      </div>
      <div className="mt-4 space-y-3" data-testid="job-quote-drawings">
        {quoteAreas.filter((a) => a.pieces.length).length === 0 ? (
          <p className="text-xs text-zinc-500">
            {linkedQuote === undefined
              ? "Loading the quote…"
              : "No drawing on this quote yet."}
          </p>
        ) : (
          quoteAreas
            .filter((a) => a.pieces.length)
            .map((a) => (
              <div
                key={a.id}
                data-testid={`job-quote-drawing-${a.id}`}
                className="rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  {a.name || "Area"}
                  {a.slab_color && (
                    <span className="ml-2 normal-case tracking-normal text-slate-500">
                      · {a.slab_color}
                    </span>
                  )}
                </div>
                <DesignerPlan
                  pieces={a.pieces}
                  light
                  edgeStyles={[]}
                  title={{
                    project: job.project_name || job.code,
                    customer: job.customer_name,
                    area: a.name,
                    date: new Date().toLocaleDateString(),
                  }}
                />
              </div>
            ))
        )}
      </div>
    </div>
  ) : null;

  const filesCard = (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-files-card"
    >
      <TemplatorCard jobId={id} job={job} onChange={setJob} />
      <div className="mt-5 flex overflow-hidden rounded-md border border-zinc-800">
        {[
          ["photos", "Job Photos"],
          ["finished", "Finished Photos"],
          ["docs", "Job Documents"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`job-files-tab-${k}`}
            onClick={() => setFilesTab(k)}
            className={`h-11 flex-1 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
              filesTab === k
                ? "bg-indigo-500 text-white"
                : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mt-3 [&>div]:border-0 [&>div]:bg-transparent [&>div]:p-0">
        {filesTab === "photos" ? (
          <JobPhotos jobId={id} job={job} onChange={setJob} />
        ) : filesTab === "finished" ? (
          <JobFinishedPhotos jobId={id} />
        ) : (
          <FileVault entity="jobs" entityId={id} title="Job Documents" />
        )}
      </div>
    </div>
  );

  const statusBadges = (sfx = "") => (
    <>
      {!embedded && (
        <StageBadge stage={job.stage} testId={`job-detail-stage${sfx}`} />
      )}
      {readyToComplete && (
        <button
          type="button"
          data-testid={`job-ready-complete${sfx}`}
          onClick={() => setStage("Complete")}
          title="All field stops are done — mark this job Complete"
          className="inline-flex items-center gap-2 rounded-full border border-emerald-500/50 bg-emerald-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-300 transition-colors duration-150 hover:bg-emerald-500/25"
        >
          <CheckCircle size={13} weight="fill" /> Ready to mark complete
        </button>
      )}
      {job.awaiting_deposit && (
        <span
          data-testid={`job-deposit-unpaid${sfx}`}
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-300"
        >
          <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400" />
          Deposit unpaid
        </span>
      )}
      {!job.awaiting_deposit && job.deposit_paid_at && (
        <span
          data-testid={`job-deposit-paid${sfx}`}
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-300"
        >
          <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-400" />
          Deposit paid
        </span>
      )}
      <DrawingApprovalBadge
        approval={drawingApproval}
        testid={`job-drawing-approval-badge${sfx}`}
      />
      <StageAge job={job} testId={`job-detail-age${sfx}`} />
    </>
  );

  return (
    <div className={embedded ? "" : "grain min-h-screen"} data-testid={embedded ? "project-field-work" : undefined}>
      {!embedded && <AppHeader title={job.code} />}
      <WorkflowDialog
        open={workflowOpen}
        onOpenChange={(v) => {
          setWorkflowOpen(v);
          if (!v) setWorkflowFocus("");
        }}
        focusStage={workflowFocus}
        job={job}
        crewList={crewList}
        onSaved={(data) => setJob(data)}
      />
      {!embedded && (
      <AssistantDock
        context={{
          kind: "job",
          id: id,
          label: job.code,
          prompts: [
            `Add a crew note to ${job.code}: `,
            `Reschedule ${job.code}'s install to `,
            `Add an undermount sink fixture to ${job.code} in the kitchen`,
          ],
        }}
      />
      )}
      <Wrap className={embedded ? "relative z-10 min-w-0 max-w-full overflow-x-clip" : "relative z-10 mx-auto max-w-5xl px-4 pb-24 pt-6"}>
        {!embedded && (
        <button
          onClick={() => navigate("/projects")}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Stage board
        </button>
        )}

        <div
          className={embedded ? "rounded-lg border border-zinc-800 bg-zinc-900 p-4" : "rounded-lg bg-zinc-900 p-5"}
          style={{ boxShadow: "none" }}
          data-testid="job-header-card"
        >
          {drawingApproval?.has_drawing &&
            (drawingApproval.status === "change_requested" ? (
              <div
                data-testid="job-drawing-approval"
                className="mb-4 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4"
              >
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-amber-200">
                  <ChatCircle
                    size={18}
                    weight="fill"
                    className="shrink-0 text-amber-400"
                  />
                  Customer requested a drawing change
                  {drawingApproval.quote_id && (
                    <Link
                      to={`/quotes/${drawingApproval.quote_id}`}
                      data-testid="job-drawing-approval-quote"
                      className="ml-auto text-xs normal-case tracking-normal underline"
                    >
                      {drawingApproval.quote_code}
                    </Link>
                  )}
                </div>
                {drawingApproval.note && (
                  <p
                    data-testid="job-drawing-change-note"
                    className="mt-2.5 border-l-2 border-amber-500/60 pl-3 text-base italic leading-relaxed text-amber-50"
                  >
                    &ldquo;{drawingApproval.note}&rdquo;
                  </p>
                )}
                <div className="mt-2.5 text-xs text-amber-300/80">
                  {drawingApproval.name ? `— ${drawingApproval.name}` : ""}
                  {drawingApproval.name && drawingApproval.at ? " · " : ""}
                  {drawingApproval.at
                    ? new Date(drawingApproval.at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : ""}
                  {" — nothing is cut until they approve the updated drawing."}
                </div>
                <div
                  data-testid="job-reprice-flag"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 bg-amber-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-200"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  May change sq ft / price — re-quote
                </div>
              </div>
            ) : drawingApproval.status === "approved" ? (
              <div
                data-testid="job-drawing-approval"
                className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200"
              >
                {`Drawing approved by ${drawingApproval.name} on ${new Date(
                  drawingApproval.at,
                ).toLocaleDateString()}`}
                {drawingApproval.quote_id && (
                  <Link
                    to={`/quotes/${drawingApproval.quote_id}`}
                    data-testid="job-drawing-approval-quote"
                    className="ml-2 underline"
                  >
                    {drawingApproval.quote_code}
                  </Link>
                )}
              </div>
            ) : null)}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {embedded ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="job-embedded-strip">
                  <span className="font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                    {job.code}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-300">
                    Field work
                  </span>
                  {job.appt_confirmed_at && (
                    <span
                      data-testid="job-detail-confirmed"
                      className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300"
                    >
                      Confirmed by text
                    </span>
                  )}
                  {contactPhone && (
                    <a
                      href={`tel:${contactPhone.replace(/[^\d+]/g, "")}`}
                      data-testid="job-contact-call"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-200"
                      title={`Call ${contactName}`}
                    >
                      <Phone size={13} weight="bold" /> {contactPhone}
                    </a>
                  )}
                  <div className="flex w-full flex-wrap gap-1.5 pt-1" data-testid="job-embedded-stats">
                    {schedule.map(([label, date, win, crew, time]) => {
                      const set = !!date;
                      const when = set
                        ? new Date(`${date}T12:00:00`).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })
                        : "not set";
                      const extra = [time ? fmtTime12(time) : "", win && win !== "All day" ? win : ""].filter(Boolean).join(" ");
                      return (
                        <button
                          type="button"
                          key={label}
                          onClick={() => {
                            setWorkflowFocus({ Template: "template", Fabrication: "fab", Install: "install" }[label] || "");
                            setWorkflowOpen(true);
                          }}
                          title={set ? `Edit ${label} schedule` : `Schedule ${label}`}
                          data-testid={`job-stat-${label.toLowerCase()}`}
                          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-left text-[11px] transition-colors duration-150 hover:border-indigo-500 ${
                            set
                              ? "border-zinc-700 bg-zinc-950/60 text-zinc-200"
                              : "border-dashed border-zinc-700 text-zinc-500 hover:text-indigo-200"
                          }`}
                        >
                          <span className="font-bold uppercase tracking-wider text-zinc-500">{label}</span>
                          <span className="font-semibold">{set ? when : "not set · tap to schedule"}</span>
                          {extra && <span className="text-zinc-400">{extra}</span>}
                          {crew && (
                            <span className="rounded bg-indigo-500/15 px-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-200">
                              {crew}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
              <div className="font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                {job.code}
              </div>
              {job.appt_confirmed_at && (
                <span
                  data-testid="job-detail-confirmed"
                  className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300"
                >
                  Confirmed by text
                </span>
              )}
              <div
                className="mt-1 truncate text-2xl font-bold tracking-tight sm:text-3xl"
                data-testid="job-detail-customer"
              >
                {job.customer_id ? (
                  <Link
                    to={`/customers/${job.customer_id}`}
                    data-testid="job-customer-link"
                    className="text-indigo-300 transition-colors duration-150 hover:text-indigo-200"
                  >
                    {companyName || job.customer_name}
                  </Link>
                ) : (
                  <span className="text-zinc-100">
                    {companyName || job.customer_name || "No customer linked"}
                  </span>
                )}
              </div>
              {companyName && contactName && contactName !== companyName && (
                <div
                  className="mt-0.5 flex items-center gap-1.5 truncate text-sm font-medium text-zinc-300"
                  data-testid="job-detail-contact"
                >
                  <User
                    size={15}
                    weight="bold"
                    className="shrink-0 text-zinc-500"
                  />
                  {contactName}
                </div>
              )}
              {contactPhone && (
                <a
                  href={`tel:${contactPhone.replace(/[^\d+]/g, "")}`}
                  data-testid="job-contact-call"
                  className="mt-0.5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 transition-colors duration-150 hover:text-emerald-200"
                  title={`Call ${contactName}`}
                >
                  <Phone size={14} weight="bold" className="shrink-0" />
                  {contactPhone}
                </a>
              )}
              <h1
                className="mt-1 truncate text-sm font-semibold text-zinc-400"
                data-testid="job-detail-project"
              >
                {job.project_name}
              </h1>
                </>
              )}
              <div
                className={`flex flex-wrap items-center gap-x-4 gap-y-2 sm:hidden ${embedded ? "mt-2" : "mt-3"}`}
                data-testid="job-status-row-mobile"
              >
                {statusBadges("-m")}
              </div>
            </div>
            <div className="flex shrink-0 items-start gap-2">
              <div className="hidden flex-col items-end gap-2 sm:flex">
                {statusBadges("")}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="job-actions-menu"
                    title="Job actions"
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-zinc-800 text-zinc-300 transition-colors duration-150 hover:border-zinc-700 sm:hidden"
                  >
                    <DotsThreeVertical size={20} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  data-testid="job-actions-menu-content"
                  className="w-56 border-zinc-800 bg-zinc-900"
                >
                  {(job.stage === "Complete" || job.stage === "Install") && (
                    <DropdownMenuItem
                      data-testid="menu-job-service"
                      onClick={() => navigate(`/projects/service/new?job=${id}`)}
                    >
                      <Wrench size={15} weight="bold" className="mr-2" /> New
                      service call
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-testid="menu-job-archive"
                    disabled={!job.archived && job.stage !== "Complete"}
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/jobs/${id}/archive`, { archived: !job.archived });
                        setJob(data);
                        toast.success(data.archived ? "Job archived" : "Job restored");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not archive");
                      }
                    }}
                  >
                    <Archive size={15} weight="bold" className="mr-2" />
                    {job.archived
                      ? "Restore from archive"
                      : job.stage === "Complete"
                        ? "Archive job (needs paid in full)"
                        : "Archive (Complete jobs only)"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-job-move-account"
                    onClick={() => setMoveOpen(true)}
                  >
                    <Archive size={15} weight="bold" className="mr-2" /> Move to another account
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-job-edit"
                    onClick={() => navigate(`/projects/${id}/edit`)}
                  >
                    <PencilSimple size={15} weight="bold" className="mr-2" /> Edit
                    job
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-job-packet"
                    onClick={() => navigate(`/projects/${id}/packet`)}
                  >
                    <Printer size={15} weight="bold" className="mr-2" /> Work
                    order packet
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-job-install-label"
                    onClick={() => setLabelDlg({ kind: "Install" })}
                  >
                    <Printer size={15} weight="bold" className="mr-2" /> Print
                    install label (4×6)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-job-template-label"
                    onClick={() => setLabelDlg({ kind: "Template" })}
                  >
                    <Printer size={15} weight="bold" className="mr-2" /> Print
                    template label (4×6)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-job-create-quote"
                    onClick={createQuote}
                  >
                    <Receipt size={15} weight="bold" className="mr-2" /> Create
                    quote from job
                  </DropdownMenuItem>
                  {job.stonify_url?.trim() && (
                    <DropdownMenuItem
                      data-testid="menu-job-stonify"
                      onClick={() => window.open(job.stonify_url, "_blank")}
                    >
                      <ArrowSquareOut size={15} weight="bold" className="mr-2" />{" "}
                      Open Stonify
                    </DropdownMenuItem>
                  )}
                  {job.raptor_url?.trim() && (
                    <DropdownMenuItem
                      data-testid="menu-job-raptor"
                      onClick={() => window.open(job.raptor_url, "_blank")}
                    >
                      <ArrowSquareOut size={15} weight="bold" className="mr-2" />{" "}
                      Open Raptor
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem
                    data-testid="menu-job-delete"
                    onClick={() => setDelOpen(true)}
                    className="text-red-400 focus:text-red-300"
                  >
                    <Trash size={15} weight="bold" className="mr-2" /> Delete job
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div
            className="mt-5 hidden flex-wrap items-center gap-2 border-t border-zinc-800 pt-4 sm:flex"
            data-testid="job-actions"
          >
            {nextStage && !embedded && (
              <Button
                data-testid="advance-stage-button"
                onClick={() => setStage(nextStage)}
                className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                Move to {nextStage}{" "}
                <ArrowRight size={15} weight="bold" className="ml-2" />
              </Button>
            )}
            {job.stage === "Complete" || job.stage === "Install" ? (
              <Button
                data-testid="log-service-call-button"
                variant="outline"
                onClick={() => navigate(`/projects/service/new?job=${id}`)}
                className="h-10 border-orange-500/40 bg-transparent px-4 text-[11px] uppercase tracking-widest text-orange-300 transition-colors duration-150 hover:bg-orange-500/10"
              >
                <Wrench size={15} weight="bold" className="mr-2" /> Service Call
              </Button>
            ) : null}
            <Button
              data-testid="edit-job-button"
              variant="outline"
              onClick={() => navigate(`/projects/${id}/edit`)}
              className="h-10 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <PencilSimple size={15} weight="bold" className="mr-2" /> Edit
            </Button>
            <Button
              data-testid="job-packet-button"
              variant="outline"
              onClick={() => navigate(`/projects/${id}/packet`)}
              className="h-10 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <Printer size={15} weight="bold" className="mr-2" /> Packet
            </Button>
            <Button
              data-testid="job-create-quote-button"
              variant="outline"
              onClick={createQuote}
              className="h-10 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <Receipt size={15} weight="bold" className="mr-2" /> Create quote
            </Button>
            {job.quote_id && (
              <Button
                data-testid="job-view-quote-button"
                variant="outline"
                onClick={() => navigate(`/quotes/${job.quote_id}/design`)}
                className="h-10 border-indigo-500/40 bg-indigo-500/10 px-4 text-[11px] uppercase tracking-widest text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/20"
              >
                <Receipt size={15} weight="bold" className="mr-2" /> Quote{" "}
                {job.quote_code || ""}
              </Button>
            )}
            {job.stonify_url?.trim() && (
              <a
                href={job.stonify_url}
                target="_blank"
                rel="noreferrer"
                data-testid="job-stonify-link"
                className="flex h-10 items-center gap-2 rounded-md border border-blue-500/40 bg-transparent px-4 text-[11px] uppercase tracking-widest text-blue-300 transition-colors duration-150 hover:bg-blue-500/10"
              >
                <ArrowSquareOut size={15} weight="bold" /> Stonify
              </a>
            )}
            {job.raptor_url?.trim() && (
              <a
                href={job.raptor_url}
                target="_blank"
                rel="noreferrer"
                data-testid="job-raptor-link"
                className="flex h-10 items-center gap-2 rounded-md border border-orange-500/40 bg-transparent px-4 text-[11px] uppercase tracking-widest text-orange-300 transition-colors duration-150 hover:bg-orange-500/10"
              >
                <ArrowSquareOut size={15} weight="bold" /> Raptor
              </a>
            )}
            <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
              <button
                data-testid="delete-job-button"
                title="Delete job"
                onClick={() => setDelOpen(true)}
                className="ml-auto inline-flex h-10 items-center gap-1.5 px-2 text-[11px] uppercase tracking-widest text-zinc-600 transition-colors duration-150 hover:text-red-400"
              >
                <Trash size={14} weight="bold" /> Delete
              </button>
              <AlertDialogContent className="border-zinc-800 bg-zinc-900">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {job.code}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    This permanently removes the field work, its notes and its schedule.
                    This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    data-testid="cancel-job-delete"
                    className="border-zinc-700"
                  >
                    Keep job
                  </AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="confirm-job-delete"
                    onClick={remove}
                    className="bg-red-600 text-white hover:bg-red-500"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6">
          <SegTabs sticky testId="job-tabs" tabs={TABS} value={tab} onChange={(t) => setTab(t.key)} />
        </div>

        {/* Panels */}
        <div className="mt-6" data-testid={`job-tab-panel-${tab}`} {...swipe}>
          {tab === "overview" && (
            <div className="space-y-6">
              <ApprovalTrail quote={linkedQuote} />
              <div className="grid gap-6 lg:grid-cols-2">
                {summaryCard}
                {stageHistoryCard}
              </div>
              <ProfitCard jobId={id} />
              {materialCard}
            </div>
          )}

          {tab === "schedule" && (
            <div className="space-y-6">
              {addressCard}
              {scheduleCard}
              <JobFieldRecords job={job} />
            </div>
          )}

          {tab === "materials" && (
            <div className="space-y-6">
              {reservedCard}
              <div className="grid gap-6 lg:grid-cols-2">
                {sinkCard}
                {poCard}
              </div>
              <FixturesPanel
                kind="jobs"
                id={id}
                fixtures={job.fixtures || []}
                onUpdated={setJob}
                context={{
                  kind: "Job",
                  title: job.project_name,
                  customer: job.customer_name,
                  code: job.code,
                  address: job.address,
                  url: `${window.location.origin}/admin/projects/${id}?tab=field`,
                }}
              />
            </div>
          )}

          {tab === "comms" && (
            <div className="space-y-6">
              {messagesCard}
              <PortalThread kind="job" recordId={id} />
            </div>
          )}

          {tab === "files" && (
            <div className="space-y-4" data-testid="job-files-pane">
              {quoteCard}
              {filesCard}
            </div>
          )}
        </div>
      </Wrap>

      {nextStage && !embedded && (
        <div
          data-testid="job-mobile-stage-bar"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-800 bg-zinc-900/95 p-3 backdrop-blur sm:hidden"
        >
          <Button
            data-testid="advance-stage-button-mobile"
            onClick={() => setStage(nextStage)}
            className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            Move to {nextStage}{" "}
            <ArrowRight size={15} weight="bold" className="ml-2" />
          </Button>
        </div>
      )}
      <LabelDialog
        jobId={id}
        kind={labelDlg?.kind || "Install"}
        open={!!labelDlg}
        onOpenChange={(o) => !o && setLabelDlg(null)}
      />
      {job && (
        <MoveAccountDialog
          open={moveOpen}
          onClose={() => setMoveOpen(false)}
          onDone={() => api.get(`/jobs/${id}`).then(({ data }) => setJob(data))}
          endpoint={`/projects/${id}/move-account`}
          title="Move job to another account"
          description="Moves this job with its project, quotes and invoices."
          confirmLabel="Move job"
          excludeId={job.customer_id}
        />
      )}
    </div>
  );
}
